import { FhirResourceTypes } from '../constants/FhirResourceTypes';
import { FhirBinary } from '../types/FhirBinary';
import moment from 'moment';
import dateUtils from './dateUtils';

const extractAllergyItems = (entries: any): string[] => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    let text = entry.resource?.code?.text?.trim();
    const allergyStatus = entry.resource?.clinicalStatus?.coding?.[0]?.code;
    const allergyCategory = entry.resource?.category?.[0];
    const allergyCriticality = entry.resource?.criticality;
    const allergyLastUpdated = entry.resource?.meta?.lastUpdated;

    if (text && !uniqueItems.includes(text)) {
      let outputStr = text;
      if (allergyStatus) outputStr += ` | clinical status: ${allergyStatus}`;
      if (allergyCategory) outputStr += ` | type: ${allergyCategory}`;
      if (allergyCriticality) outputStr += ` | criticality: ${allergyCriticality}`;
      if (allergyLastUpdated) {
        const formattedDate = moment(allergyLastUpdated).format('YYYY-MM-DD');
        outputStr += ` | date: ${formattedDate}`;
      }

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractClinicalConditions = (entries: any): string[] => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const clinicalText = entry.resource?.code?.text?.trim();
    const clinicalCategory = entry.resource?.category?.[0]?.coding?.[0]?.code;
    const clinicalSeverity = entry.resource?.severity?.coding?.[0]?.display;
    const clinicalBodySite = entry.resource?.bodySite?.[0]?.coding?.[0]?.display;
    const clinicalOnsetDateTime = entry.resource?.onsetDateTime;

    if (clinicalText && !uniqueItems.includes(clinicalText)) {
      let outputStr = clinicalText;
      if (clinicalCategory) outputStr += ` | category: ${clinicalCategory}`;
      if (clinicalSeverity) outputStr += ` | severity: ${clinicalSeverity}`;
      if (clinicalBodySite) outputStr += ` | body site: ${clinicalBodySite}`;
      if (clinicalOnsetDateTime) {
        const formattedDate = moment(clinicalOnsetDateTime).format('YYYY-MM-DD');
        outputStr += ` | date: ${formattedDate}`;
      }

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractFamilMemberHistoryItems = (entries: any, references: any): string[] => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const reference = references?.find(
      (r: any) => resource?.patient?.reference === `${FhirResourceTypes.Patient}/${r.data?.id}`
    );

    const historyName = reference?.data?.name?.[0];
    const historyText = historyName ? `${historyName?.given?.[0]} ${historyName?.family}` : '';

    const historyLastUpdated = resource?.meta?.lastUpdated;

    const historyRelationship = resource?.relationship?.text;

    const age = resource?.ageAge
      ? `${resource.ageAge.value} ${resource.ageAge.unit ?? ''}`.trim()
      : resource?.ageRange
      ? `${resource.ageRange.low?.value ?? ''}-${resource.ageRange.high?.value ?? ''} ${
          resource.ageRange.low?.unit ?? ''
        }`.trim()
      : resource?.ageString
      ? resource.ageString
      : undefined;

    const deceasedBoolean = resource?.deceasedBoolean;
    const deceasedAge = resource?.deceasedAge
      ? `${resource.deceasedAge.value} ${resource.deceasedAge.unit ?? ''}`.trim()
      : undefined;
    const deceasedDate = resource?.deceasedDate;
    const deceased = deceasedBoolean
      ? 'yes'
      : deceasedAge
      ? `age: ${deceasedAge}`
      : deceasedDate
      ? `date: ${deceasedDate}`
      : undefined;

    const conditionSummary = resource?.condition
      ?.map((c: any) => {
        const condText = c.code?.text || c.code?.coding?.[0]?.display;

        const onset = c.onsetAge
          ? (() => {
              const unit = c.onsetAge.unit ?? '';
              const ageStr = unit.startsWith(String(c.onsetAge.value))
                ? unit
                : `${c.onsetAge.value} ${unit}`.trim();
              return `onset age: ${ageStr}`;
            })()
          : c.onsetRange
          ? `onset range: ${c.onsetRange.low?.value ?? ''}-${c.onsetRange.high?.value ?? ''} ${
              c.onsetRange.low?.unit ?? ''
            }`.trim()
          : c.onsetPeriod
          ? `onset period: ${c.onsetPeriod.start ?? ''} - ${c.onsetPeriod.end ?? ''}`.trim()
          : c.onsetString
          ? `onset: ${c.onsetString}`
          : undefined;

        return [condText, onset].filter(Boolean).join(', ');
      })
      .filter(Boolean)
      .join('; ');

    if (historyText && !uniqueItems.includes(historyText)) {
      let outputStr = historyText;

      if (historyLastUpdated) {
        const formattedDate = moment(historyLastUpdated).format('YYYY-MM-DD');
        outputStr += ` | date: ${formattedDate}`;
      }
      if (historyRelationship) outputStr += ` | relationship: ${historyRelationship}`;
      if (age) outputStr += ` | age: ${age}`;
      if (deceased) outputStr += ` | deceased: ${deceased}`;
      if (conditionSummary) outputStr += ` | condition: ${conditionSummary}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractMedicationItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    if (
      resource?.resourceType !== FhirResourceTypes.MedicationRequest &&
      resource?.resourceType !== FhirResourceTypes.MedicationStatement
    ) {
      return;
    }

    const medtext = resource?.medicationCodeableConcept?.text?.trim();
    const medCoding = resource?.medicationCodeableConcept?.coding?.[0];
    const medCode = medCoding?.code;
    const medStatus = resource?.status;
    const isActive = medStatus === 'active';

    const dosageEntry = resource?.dosageInstruction?.[0] ?? resource?.dosage?.[0];

    const patientInstruction = dosageEntry?.patientInstruction;
    const asNeeded = dosageEntry?.asNeededBoolean;
    const route =
      dosageEntry?.route?.text ||
      dosageEntry?.route?.coding?.[0]?.display ||
      dosageEntry?.route?.coding?.[0]?.code;

    const doseAndRate = dosageEntry?.doseAndRate?.[0];
    const doseValue = doseAndRate?.doseQuantity?.value;
    const doseUnit = doseAndRate?.doseQuantity?.unit;
    const doseQuantity =
      doseValue !== undefined && doseUnit ? `${doseValue} ${doseUnit}` : undefined;
    const medDosage = !doseQuantity ? dosageEntry?.text : undefined;

    const strengthMatch = medCoding?.display?.match(/(\d+\.?\d*\s*(MG|MCG|MEQ|ML|UNIT|IU))/i);
    const strength = strengthMatch ? strengthMatch[0].trim() : undefined;

    const effectivePeriod = resource?.effectivePeriod
      ? resource.effectivePeriod.end &&
        resource.effectivePeriod.end !== resource.effectivePeriod.start
        ? `${resource.effectivePeriod.start} – ${resource.effectivePeriod.end}`
        : resource.effectivePeriod.start
      : undefined;

    const informationSource =
      resource?.informationSource?.display || resource?.informationSource?.reference;

    if (medtext && !uniqueItems.includes(medtext)) {
      let outputStr = medtext;

      if (medCode) outputStr += ` | code: ${medCode}`;
      if (medDosage) outputStr += ` | dosage: ${medDosage}`;
      if (doseQuantity) outputStr += ` | doseQuantity: ${doseQuantity}`;
      if (strength) outputStr += ` | strength: ${strength}`;
      if (route) outputStr += ` | route: ${route}`;
      if (patientInstruction) outputStr += ` | instructions: ${patientInstruction}`;
      if (asNeeded !== undefined) outputStr += ` | asNeeded: ${asNeeded}`;
      if (medStatus) outputStr += ` | status: ${medStatus}`;
      if (isActive !== undefined) outputStr += ` | isActive: ${isActive}`;
      if (effectivePeriod) outputStr += ` | effectivePeriod: ${effectivePeriod}`;
      if (informationSource) outputStr += ` | informationSource: ${informationSource}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractDefaultItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const text = entry.resource?.code?.text?.trim();
    if (text && !uniqueItems.includes(text)) {
      uniqueItems.push(text);
    }
  });

  return uniqueItems;
};

const ALLOWED_OBSERVATION_CATEGORIES = [
  'laboratory',
  'imaging',
  'social-history',
  'alcohol-use',
  'tobacco-use'
];

const extractObservationItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    // Filter: only allow specified categories
    const categoryCode =
      resource?.category?.[0]?.coding?.[0]?.code?.toLowerCase() ||
      resource?.category?.[0]?.text?.toLowerCase();

    if (!categoryCode || !ALLOWED_OBSERVATION_CATEGORIES.includes(categoryCode)) {
      return;
    }

    const loincCode = resource?.code?.coding?.[0]?.code;
    const obsDisplayText =
      resource?.code?.text?.trim() || resource?.code?.coding?.[0]?.display?.trim();

    const obsEffectiveDateTime = resource?.effectiveDateTime;
    const obsEffectivePeriod = resource?.effectivePeriod
      ? `${resource.effectivePeriod.start ?? ''} - ${resource.effectivePeriod.end ?? ''}`.trim()
      : undefined;
    const effective = obsEffectiveDateTime || obsEffectivePeriod;

    const obsCategory = resource?.category?.[0]?.text?.trim() || categoryCode;

    // value[x]
    const value = resource?.valueQuantity
      ? `${resource.valueQuantity.value} ${resource.valueQuantity.unit ?? ''}`.trim()
      : resource?.valueCodeableConcept
      ? resource.valueCodeableConcept.text || resource.valueCodeableConcept.coding?.[0]?.display
      : resource?.valueString
      ? resource.valueString
      : resource?.valueBoolean !== undefined
      ? `${resource.valueBoolean}`
      : resource?.valueInteger !== undefined
      ? `${resource.valueInteger}`
      : resource?.valueRange
      ? `${resource.valueRange.low?.value ?? ''}-${resource.valueRange.high?.value ?? ''} ${
          resource.valueRange.low?.unit ?? ''
        }`.trim()
      : resource?.valueRatio
      ? `${resource.valueRatio.numerator?.value ?? ''}/${
          resource.valueRatio.denominator?.value ?? ''
        }`
      : undefined;

    const obsComponent = resource?.component;

    if (obsDisplayText && !uniqueItems.includes(obsDisplayText)) {
      // Fixed: append loincCode to display text instead of pushing separately
      let outputStr = loincCode ? `${obsDisplayText}, ${loincCode}` : obsDisplayText;

      if (obsCategory) outputStr += ` | category: ${obsCategory}`;
      if (effective) outputStr += ` | effective: ${effective}`;
      if (value) outputStr += ` | value: ${value}`;

      if (obsComponent) {
        obsComponent.forEach((c: any) => {
          const componentCode = c.code?.coding?.[0]?.code;
          const componentDisplay = c.code?.coding?.[0]?.display;
          const compValue = c.valueQuantity?.value;
          const compUnit = c.valueQuantity?.unit;

          outputStr += `, ${componentCode} - ${componentDisplay} ${compValue} ${compUnit}`;
        });
      }

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractImmunizationItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const immText = resource?.vaccineCode?.text?.trim();

    const immOccurrenceDateTime = resource?.occurrenceDateTime;

    const immDoseQuantity = resource?.doseQuantity
      ? `${resource.doseQuantity.value} ${resource.doseQuantity.unit ?? ''}`.trim()
      : undefined;

    const immReactionSummary = resource?.reaction
      ?.map((r: any) => {
        const detail = r.detail?.display || r.detail?.reference;
        const date = r.date;
        const reported = r.reported !== undefined ? `reported: ${r.reported}` : undefined;
        return [detail, date, reported].filter(Boolean).join(', ');
      })
      .filter(Boolean)
      .join('; ');

    const immProtocolSummary = resource?.protocolApplied
      ?.map((p: any) => {
        const dose = p.doseNumberString ?? p.doseNumberPositiveInt;
        const diseases = p.targetDisease
          ?.map((td: any) => td.text || td.coding?.[0]?.display)
          .filter(Boolean)
          .join(', ');
        return `${p.series ?? ''} dose ${dose ?? ''} (${diseases ?? ''})`.trim();
      })
      .join('; ');

    if (immText && !uniqueItems.includes(immText)) {
      let outputStr = immText;
      if (immOccurrenceDateTime) outputStr += ` | occurrenceDateTime: ${immOccurrenceDateTime}`;
      if (immDoseQuantity) outputStr += ` | doseQuantity: ${immDoseQuantity}`;
      if (immReactionSummary) outputStr += ` | reaction: ${immReactionSummary}`;
      if (immProtocolSummary) outputStr += ` | protocolApplied: ${immProtocolSummary}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractProcedureItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const procResource = entry.resource;

    const getCodingText = (obj: any) =>
      obj?.text || obj?.coding?.[0]?.display || obj?.coding?.[0]?.code;

    const getReferenceText = (ref: any) => ref?.display || ref?.reference;

    const text = procResource?.code?.coding?.[0]?.text?.trim() || getCodingText(procResource?.code);

    const basedOn = procResource?.basedOn?.map(getReferenceText).filter(Boolean).join(', ');

    const partOf = procResource?.partOf?.map(getReferenceText).filter(Boolean).join(', ');

    const status = procResource?.status;

    const statusReason = getCodingText(procResource?.statusReason);

    const category = getCodingText(procResource?.category);

    const performed =
      procResource?.performedDateTime ||
      (procResource?.performedPeriod
        ? `${procResource.performedPeriod.start ?? ''} - ${
            procResource.performedPeriod.end ?? ''
          }`.trim()
        : procResource?.performedString);

    const reasonCode = procResource?.reasonCode?.map(getCodingText).filter(Boolean).join(', ');

    const bodySite = procResource?.bodySite?.map(getCodingText).filter(Boolean).join(', ');

    const outcome = getCodingText(procResource?.outcome);

    const report = procResource?.report?.map(getReferenceText).filter(Boolean).join(', ');

    const complication = procResource?.complication?.map(getCodingText).filter(Boolean).join(', ');

    const complicationDetail = procResource?.complicationDetail
      ?.map(getReferenceText)
      .filter(Boolean)
      .join(', ');

    const followUp = procResource?.followUp?.map(getCodingText).filter(Boolean).join(', ');

    const note = procResource?.note
      ?.map((n: any) => n.text)
      .filter(Boolean)
      .join(' | ');

    const focalDevice = procResource?.focalDevice
      ?.map((fd: any) => getReferenceText(fd.manipulated))
      .filter(Boolean)
      .join(', ');

    const usedReference = procResource?.usedReference
      ?.map(getReferenceText)
      .filter(Boolean)
      .join(', ');

    const usedCode = procResource?.usedCode?.map(getCodingText).filter(Boolean).join(', ');

    if (text && !uniqueItems.includes(text)) {
      let outputStr = text;

      if (status) outputStr += ` | status: ${status}`;
      if (statusReason) outputStr += ` | statusReason: ${statusReason}`;
      if (category) outputStr += ` | category: ${category}`;
      if (performed) outputStr += ` | performed: ${performed}`;
      if (basedOn) outputStr += ` | basedOn: ${basedOn}`;
      if (partOf) outputStr += ` | partOf: ${partOf}`;
      if (reasonCode) outputStr += ` | reasonCode: ${reasonCode}`;
      if (bodySite) outputStr += ` | bodySite: ${bodySite}`;
      if (outcome) outputStr += ` | outcome: ${outcome}`;
      if (report) outputStr += ` | result: ${report}`;
      if (complication) outputStr += ` | complication: ${complication}`;
      if (complicationDetail) outputStr += ` | complicationDetail: ${complicationDetail}`;
      if (followUp) outputStr += ` | followUp: ${followUp}`;
      if (note) outputStr += ` | note: ${note}`;
      if (focalDevice) outputStr += ` | focalDevice: ${focalDevice}`;
      if (usedReference) outputStr += ` | usedReference: ${usedReference}`;
      if (usedCode) outputStr += ` | usedCode: ${usedCode}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractDiagnosticReportItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const getCodingText = (obj: any) =>
      obj?.text || obj?.coding?.[0]?.display || obj?.coding?.[0]?.code;

    const getReferenceText = (ref: any) => ref?.display || ref?.reference;

    const text = resource?.code?.text?.trim() || getCodingText(resource?.code);

    const basedOn = resource?.basedOn?.map(getReferenceText).filter(Boolean).join(', ');

    const status = resource?.status;

    const encounter = getReferenceText(resource?.encounter);

    const effective =
      resource?.effectiveDateTime ||
      (resource?.effectivePeriod
        ? `${resource.effectivePeriod.start ?? ''} - ${resource.effectivePeriod.end ?? ''}`.trim()
        : undefined);

    const issued = resource?.issued;

    const result = resource?.result?.map(getReferenceText).filter(Boolean).join(', ');

    const imagingStudy = resource?.imagingStudy?.map(getReferenceText).filter(Boolean).join(', ');

    const media = resource?.media
      ?.map((m: any) => getReferenceText(m.link))
      .filter(Boolean)
      .join(', ');

    const conclusion = resource?.conclusion;

    const conclusionCode = resource?.conclusionCode?.map(getCodingText).filter(Boolean).join(', ');

    if (text && !uniqueItems.includes(text)) {
      let outputStr = text;

      if (status) outputStr += ` | status: ${status}`;
      if (basedOn) outputStr += ` | basedOn: ${basedOn}`;
      if (encounter) outputStr += ` | encounter: ${encounter}`;
      if (effective) outputStr += ` | effective: ${effective}`;
      if (issued) outputStr += ` | issued: ${issued}`;
      if (result) outputStr += ` | result: ${result}`;
      if (imagingStudy) outputStr += ` | imagingStudy: ${imagingStudy}`;
      if (media) outputStr += ` | media: ${media}`;
      if (conclusion) outputStr += ` | conclusion: ${conclusion}`;
      if (conclusionCode) outputStr += ` | conclusionCode: ${conclusionCode}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractEncounterItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const getCodingText = (obj: any) =>
      obj?.text || obj?.coding?.[0]?.display || obj?.coding?.[0]?.code;

    const getReferenceText = (ref: any) => ref?.display || ref?.reference;

    const text =
      resource?.type?.map(getCodingText).filter(Boolean).join(', ') ||
      getCodingText(resource?.serviceType) ||
      resource?.class?.display ||
      resource?.class?.code;

    const status = resource?.status;

    const classCode =
      resource?.class?.display || resource?.class?.code
        ? `${resource?.class?.display ?? resource?.class?.code}`
        : undefined;

    const type = resource?.type?.map(getCodingText).filter(Boolean).join(', ');

    const serviceType = getCodingText(resource?.serviceType);

    const priority = getCodingText(resource?.priority);

    const basedOn = resource?.basedOn?.map(getReferenceText).filter(Boolean).join(', ');

    const length = resource?.length
      ? `${resource.length.value ?? ''} ${resource.length.unit ?? ''}`.trim()
      : undefined;

    const reasonCode = resource?.reasonCode?.map(getCodingText).filter(Boolean).join(', ');

    const reasonReference = resource?.reasonReference
      ?.map(getReferenceText)
      .filter(Boolean)
      .join(', ');

    const diagnosis = resource?.diagnosis
      ?.map((d: any) => getReferenceText(d.condition))
      .filter(Boolean)
      .join(', ');

    const hospitalization = resource?.hospitalization
      ? [
          getCodingText(resource.hospitalization.admitSource),
          getCodingText(resource.hospitalization.dischargeDisposition)
        ]
          .filter(Boolean)
          .join(', ')
      : undefined;

    const participant = resource?.participant
      ?.map((p: any) => getReferenceText(p.individual))
      .filter(Boolean)
      .join(', ');

    const serviceProvider = getReferenceText(resource?.serviceProvider);

    const period = resource?.period?.start
      ? resource.period.end && resource.period.end !== resource.period.start
        ? `${resource.period.start} – ${resource.period.end}`
        : resource.period.start
      : undefined;

    if (text && !uniqueItems.includes(text)) {
      let outputStr = text;

      if (status) outputStr += ` | status: ${status}`;
      if (classCode) outputStr += ` | class: ${classCode}`;
      if (type) outputStr += ` | type: ${type}`;
      if (serviceType) outputStr += ` | serviceType: ${serviceType}`;
      if (priority) outputStr += ` | priority: ${priority}`;
      if (basedOn) outputStr += ` | basedOn: ${basedOn}`;
      if (length) outputStr += ` | length: ${length}`;
      if (reasonCode) outputStr += ` | reasonCode: ${reasonCode}`;
      if (reasonReference) outputStr += ` | reasonReference: ${reasonReference}`;
      if (diagnosis) outputStr += ` | diagnosis: ${diagnosis}`;
      if (hospitalization) outputStr += ` | hospitalization: ${hospitalization}`;
      if (participant) outputStr += ` | participant: ${participant}`;
      if (serviceProvider) outputStr += ` | serviceProvider: ${serviceProvider}`;
      if (period) outputStr += ` | period: ${period}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractItems = (entries: any, reportType: string, references: any) => {
  switch (reportType) {
    case FhirResourceTypes.Encounter:
      return extractEncounterItems(entries);
    case FhirResourceTypes.AllergyIntolerance:
      return extractAllergyItems(entries);
    case FhirResourceTypes.Condition:
      return extractClinicalConditions(entries);
    case FhirResourceTypes.FamilyMemberHistory:
      return extractFamilMemberHistoryItems(entries, references);
    case FhirResourceTypes.MedicationRequest:
    case FhirResourceTypes.MedicationStatement:
      return extractMedicationItems(entries);
    case FhirResourceTypes.Observation:
      return extractObservationItems(entries);
    case FhirResourceTypes.Immunization:
      return extractImmunizationItems(entries);
    case FhirResourceTypes.Procedure:
      return extractProcedureItems(entries);
    case FhirResourceTypes.DiagnosticReport:
      return extractDiagnosticReportItems(entries);
    default:
      return extractDefaultItems(entries);
  }
};

const processBinaryData = (
  data: any,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>
) => {
  const entries = data?.entry ?? [];
  if (!entries || entries.length === 0) return;

  const unqiueBinaryItems: FhirBinary[] = [];
  if (!unqiueBinaryItems) return;

  entries.forEach((entry: any) => {
    if (entry?.resource?.contained?.length > 0) {
      entry?.resource?.contained.forEach((resource: FhirBinary) => {
        if (resource.resourceType === FhirResourceTypes.Binary) {
          unqiueBinaryItems.push(resource);
        }
      });
    }
  });

  if (unqiueBinaryItems?.length > 0) {
    setBinaryItems((prev) => {
      const existingIds = new Set(prev.map((item) => item.id));
      const newItems = unqiueBinaryItems.filter((item) => !existingIds.has(item.id));
      return newItems.length > 0 ? [...prev, ...newItems] : prev;
    });
  }
};

const processFhirBundleResource = (
  data: any,
  setContent: React.Dispatch<React.SetStateAction<string>>,
  reportType: string,
  setSelectedClinicalReports: React.Dispatch<React.SetStateAction<string[]>>,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>,
  references: any
) => {
  const entries = data?.entry ?? [];
  if (!entries.length) return;

  const uniqueItems: string[] | undefined = extractItems(entries, reportType, references);

  //processBinaryData(data, setBinaryItems);

  if (!uniqueItems || uniqueItems?.length === 0) return;

  setSelectedClinicalReports((prev) => (prev.includes(reportType) ? prev : [...prev, reportType]));

  if (uniqueItems?.length > 0) {
    setContent((prev) => {
      const existing = prev
        .split('\n\n')
        .map((s) => s.trim())
        .filter(Boolean);

      const merged = [...existing];
      uniqueItems.forEach((item) => {
        if (!merged.includes(item)) {
          merged.push(item);
        }
      });

      return merged.join('\n\n');
    });
  }
};

const processFhirResource = (
  data: any,
  setContent: React.Dispatch<React.SetStateAction<string>>,
  reportType: string,
  setSelectedClinicalReports: React.Dispatch<React.SetStateAction<string[]>>,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>
) => {
  if (!data) return;

  const text = data?.code?.text?.trim();
  if (!text) return;

  const unqiueBinaryItems: FhirBinary[] = [];
  if (data?.contained?.length > 0) {
    data?.contained.forEach((resource: FhirBinary) => {
      if (resource.resourceType === FhirResourceTypes.Binary) {
        unqiueBinaryItems.push(resource);
      }
    });
  }

  setSelectedClinicalReports((prev) => {
    return prev.includes(reportType) ? prev : [...prev, reportType];
  });

  setContent((prev) => {
    const existing = prev
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    if (existing.includes(text)) return prev;

    return [...existing, text].join('\n');
  });

  // if (unqiueBinaryItems?.length > 0) {
  //   setBinaryItems((prev) => {
  //     const existingIds = new Set(prev.map((item) => item.id));
  //     const newItems = unqiueBinaryItems.filter((item) => !existingIds.has(item.id));
  //     return newItems.length > 0 ? [...prev, ...newItems] : prev;
  //   });
  // }
};

const extractPatientResourceRefs = (data: any) => {
  if (!data) return;
  const refs: string[] = [];

  const entries = data?.entry ?? [];
  if (!entries.length) return;

  entries.forEach((entry: any) => {
    if (entry?.resource?.patient) {
      refs.push(entry.resource.patient.reference);
    }
  });

  return refs;
};

const FhirResourceUtils = {
  processFhirBundleResource,
  processFhirResource,
  extractPatientResourceRefs
};

export default FhirResourceUtils;
